import {
  SOCIAL_POST_MAX_AUDIO_BYTES,
  SOCIAL_POST_MAX_IMAGE_AUDIO_DURATION_SEC,
  SOCIAL_POST_MAX_IMAGE_BYTES,
  SOCIAL_POST_MAX_VIDEO_BYTES,
  SOCIAL_POST_MAX_VIDEO_DURATION_SEC,
} from '../constants/socialPosts';

async function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('read-failed'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('read-failed'));
    reader.readAsDataURL(file);
  });
}

async function dataUrlToJpegBlob(dataUrl: string, maxEdge: number, quality: number): Promise<Blob> {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('image-decode-failed'));
    el.src = dataUrl;
  });

  let w = image.width;
  let h = image.height;
  if (w > maxEdge || h > maxEdge) {
    if (w >= h) {
      h = Math.round((h * maxEdge) / w);
      w = maxEdge;
    } else {
      w = Math.round((w * maxEdge) / h);
      h = maxEdge;
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('canvas-unavailable');
  }
  ctx.drawImage(image, 0, 0, w, h);
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
  });
  if (!blob) {
    throw new Error('image-encode-failed');
  }
  return blob;
}

function readMediaDurationSeconds(file: File, elementKind: 'audio' | 'video'): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement(elementKind);
    el.preload = 'metadata';
    const cleanup = () => {
      URL.revokeObjectURL(url);
      el.removeAttribute('src');
      el.load();
    };
    el.onloadedmetadata = () => {
      const duration = el.duration;
      cleanup();
      if (!Number.isFinite(duration) || duration <= 0) {
        reject(new Error(`social-${elementKind}-duration`));
        return;
      }
      resolve(duration);
    };
    el.onerror = () => {
      cleanup();
      reject(new Error(`social-${elementKind}-duration`));
    };
    el.src = url;
  });
}

export async function readSocialPostAudioDurationSec(file: File): Promise<number> {
  return readMediaDurationSeconds(file, 'audio');
}

export async function readSocialPostVideoDurationSec(file: File): Promise<number> {
  return readMediaDurationSeconds(file, 'video');
}

export function isSocialPostMediaDurationAllowed(
  durationSec: number,
  maxSec: number = SOCIAL_POST_MAX_VIDEO_DURATION_SEC,
): boolean {
  return durationSec > 0 && durationSec <= maxSec + 0.05;
}

export function isSocialPostAudioDurationAllowed(durationSec: number): boolean {
  return isSocialPostMediaDurationAllowed(durationSec, SOCIAL_POST_MAX_IMAGE_AUDIO_DURATION_SEC);
}

export function isSocialPostVideoDurationAllowed(durationSec: number): boolean {
  return isSocialPostMediaDurationAllowed(durationSec, SOCIAL_POST_MAX_VIDEO_DURATION_SEC);
}

function pickRecorderMimeType(kind: 'audio' | 'video'): string {
  const candidates =
    kind === 'audio'
      ? ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
      : ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
  for (const mime of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mime)) {
      return mime;
    }
  }
  return kind === 'audio' ? 'audio/webm' : 'video/webm';
}

function extensionForMime(mime: string): string {
  if (mime.includes('mp4')) {
    return 'mp4';
  }
  if (mime.includes('ogg')) {
    return 'ogg';
  }
  return 'webm';
}

function waitForEvent(target: EventTarget, eventName: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const onOk = () => {
      cleanup();
      resolve();
    };
    const onErr = () => {
      cleanup();
      reject(new Error('media-event-failed'));
    };
    const cleanup = () => {
      target.removeEventListener(eventName, onOk);
      target.removeEventListener('error', onErr);
    };
    target.addEventListener(eventName, onOk, { once: true });
    target.addEventListener('error', onErr, { once: true });
  });
}

async function recordMediaStreamUntil(
  stream: MediaStream,
  kind: 'audio' | 'video',
  maxSec: number,
  onStart?: () => Promise<void> | void,
): Promise<Blob> {
  if (typeof MediaRecorder === 'undefined') {
    throw new Error(`social-${kind}-trim`);
  }
  const mimeType = pickRecorderMimeType(kind);
  const chunks: BlobPart[] = [];
  const recorder = new MediaRecorder(stream, { mimeType });
  recorder.ondataavailable = (event) => {
    if (event.data && event.data.size > 0) {
      chunks.push(event.data);
    }
  };

  const stopped = new Promise<Blob>((resolve, reject) => {
    recorder.onstop = () => {
      resolve(new Blob(chunks, { type: mimeType }));
    };
    recorder.onerror = () => {
      reject(new Error(`social-${kind}-trim`));
    };
  });

  recorder.start(200);
  try {
    await onStart?.();
  } catch (err) {
    if (recorder.state !== 'inactive') {
      recorder.stop();
    }
    throw err;
  }

  await new Promise<void>((resolve) => {
    window.setTimeout(resolve, Math.round(maxSec * 1000));
  });

  if (recorder.state !== 'inactive') {
    recorder.stop();
  }
  return stopped;
}

/**
 * Trim audio to the first {@link SOCIAL_POST_MAX_IMAGE_AUDIO_DURATION_SEC} seconds when longer.
 */
export async function trimSocialPostAudioToMaxDuration(file: File): Promise<File> {
  const duration = await readSocialPostAudioDurationSec(file);
  if (isSocialPostAudioDurationAllowed(duration)) {
    return file;
  }

  const url = URL.createObjectURL(file);
  const audio = document.createElement('audio');
  audio.preload = 'auto';
  audio.src = url;
  audio.muted = false;

  try {
    await waitForEvent(audio, 'loadedmetadata');
    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) {
      throw new Error('social-audio-trim');
    }
    const ctx = new AudioCtx();
    const source = ctx.createMediaElementSource(audio);
    const dest = ctx.createMediaStreamDestination();
    source.connect(dest);

    const blob = await recordMediaStreamUntil(
      dest.stream,
      'audio',
      SOCIAL_POST_MAX_IMAGE_AUDIO_DURATION_SEC,
      async () => {
        if (ctx.state === 'suspended') {
          await ctx.resume();
        }
        audio.currentTime = 0;
        await audio.play();
      },
    );

    audio.pause();
    await ctx.close().catch(() => undefined);
    URL.revokeObjectURL(url);

    if (!blob.size) {
      throw new Error('social-audio-trim');
    }
    const mime = blob.type || pickRecorderMimeType('audio');
    return new File([blob], `story-audio.${extensionForMime(mime)}`, { type: mime });
  } catch (err) {
    URL.revokeObjectURL(url);
    audio.pause();
    if (err instanceof Error && err.message.startsWith('social-')) {
      throw err;
    }
    throw new Error('social-audio-trim');
  }
}

/**
 * Trim video to the first {@link SOCIAL_POST_MAX_VIDEO_DURATION_SEC} seconds when longer.
 */
export async function trimSocialPostVideoToMaxDuration(file: File): Promise<File> {
  const duration = await readSocialPostVideoDurationSec(file);
  if (isSocialPostVideoDurationAllowed(duration)) {
    return file;
  }

  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.preload = 'auto';
  video.playsInline = true;
  video.muted = true;
  video.src = url;

  try {
    await waitForEvent(video, 'loadedmetadata');
    type VideoCaptureEl = HTMLVideoElement & {
      captureStream?: () => MediaStream;
      mozCaptureStream?: () => MediaStream;
    };
    const videoCap = video as VideoCaptureEl;
    const captureFn =
      typeof videoCap.captureStream === 'function'
        ? videoCap.captureStream.bind(videoCap)
        : typeof videoCap.mozCaptureStream === 'function'
          ? videoCap.mozCaptureStream.bind(videoCap)
          : null;
    if (!captureFn) {
      throw new Error('social-video-trim');
    }

    video.currentTime = 0;
    const stream = captureFn();
    const blob = await recordMediaStreamUntil(
      stream,
      'video',
      SOCIAL_POST_MAX_VIDEO_DURATION_SEC,
      async () => {
        await video.play();
      },
    );

    video.pause();
    for (const track of stream.getTracks()) {
      track.stop();
    }
    URL.revokeObjectURL(url);

    if (!blob.size) {
      throw new Error('social-video-trim');
    }
    const mime = blob.type || pickRecorderMimeType('video');
    return new File([blob], `story-video.${extensionForMime(mime)}`, { type: mime });
  } catch (err) {
    URL.revokeObjectURL(url);
    video.pause();
    if (err instanceof Error && err.message.startsWith('social-')) {
      throw err;
    }
    throw new Error('social-video-trim');
  }
}

export async function prepareSocialPostImageBlob(file: File): Promise<Blob> {
  if (!file.type.startsWith('image/')) {
    throw new Error('social-image-type');
  }
  if (file.size > SOCIAL_POST_MAX_IMAGE_BYTES * 4) {
    throw new Error('social-image-too-large');
  }
  const dataUrl = await readFileAsDataUrl(file);
  let quality = 0.88;
  let blob = await dataUrlToJpegBlob(dataUrl, 1280, quality);
  while (blob.size > SOCIAL_POST_MAX_IMAGE_BYTES && quality > 0.45) {
    quality -= 0.08;
    blob = await dataUrlToJpegBlob(dataUrl, 1080, quality);
  }
  if (blob.size > SOCIAL_POST_MAX_IMAGE_BYTES) {
    throw new Error('social-image-too-large');
  }
  return blob;
}

export type PreparedSocialMediaFile = {
  file: File;
  trimmed: boolean;
};

/** Validate type/size, then trim to 15s when needed. */
export async function prepareSocialPostAudioFile(file: File): Promise<PreparedSocialMediaFile> {
  if (!file.type.startsWith('audio/')) {
    throw new Error('social-audio-type');
  }
  if (file.size > SOCIAL_POST_MAX_AUDIO_BYTES) {
    throw new Error('social-audio-too-large');
  }
  const duration = await readSocialPostAudioDurationSec(file);
  if (isSocialPostAudioDurationAllowed(duration)) {
    return { file, trimmed: false };
  }
  const trimmed = await trimSocialPostAudioToMaxDuration(file);
  return { file: trimmed, trimmed: true };
}

/** Validate type/size, then trim to 30s when needed. */
export async function prepareSocialPostVideoFile(file: File): Promise<PreparedSocialMediaFile> {
  if (!file.type.startsWith('video/')) {
    throw new Error('social-video-type');
  }
  if (file.size > SOCIAL_POST_MAX_VIDEO_BYTES) {
    throw new Error('social-video-too-large');
  }
  const duration = await readSocialPostVideoDurationSec(file);
  if (isSocialPostVideoDurationAllowed(duration)) {
    return { file, trimmed: false };
  }
  const trimmed = await trimSocialPostVideoToMaxDuration(file);
  return { file: trimmed, trimmed: true };
}
